
import React, { createContext, useContext, useEffect } from 'react';
import { ThemeProvider as NextThemesProvider } from "next-themes";

interface ThemeContextType {
  theme: 'light';
  getThemeValue: <T>(lightValue: T, darkValue: T) => T;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Create a NextThemesProvider with forced light theme
  return (
    <NextThemesProvider defaultTheme="light" forcedTheme="light" enableSystem={false} attribute="class">
      <InnerThemeProvider>
        {children}
      </InnerThemeProvider>
    </NextThemesProvider>
  );
}

function InnerThemeProvider({ children }: { children: React.ReactNode }) {
  // Apply light theme class to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('dark');
    root.classList.add('light');
    
    // Save theme preference
    localStorage.setItem('mantle-theme', 'light');

    // Add global styles to ensure dropdowns and popovers work properly
    const style = document.createElement('style');
    style.textContent = `
      /* Ensure all dialogs and popups have proper z-index stacking */
      .radix-select-content,
      .radix-popover-content,
      .radix-dialog-content {
        pointer-events: auto !important;
        z-index: 99999 !important;
      }
      
      /* Ensure portaled content appears above dialogs */
      [data-radix-portal] {
        position: relative;
        z-index: 99999 !important;
      }
      
      /* Fix for dialog overlays */
      [data-radix-dialog-overlay] {
        z-index: 9000 !important;
      }
      
      /* Ensure dialog content appears above overlay */
      [data-radix-dialog-content] {
        z-index: 9100 !important;
      }

      /* Ensure all interactive elements have pointer events */
      button, input, select, textarea, [role="button"], 
      [role="combobox"], [role="listbox"], [role="option"] {
        pointer-events: auto !important;
      }
      
      /* Fix for date inputs */
      input[type="date"] {
        position: relative;
        z-index: 10 !important;
        pointer-events: auto !important;
      }
      
      /* Ensure calendar popover appears properly */
      .rdp {
        position: relative;
        z-index: 999999 !important;
        pointer-events: auto !important;
      }

      /* Make sure day picker buttons are clickable */
      .rdp-button {
        pointer-events: auto !important;
      }

      /* Fix for calendar navigation buttons */
      .rdp-nav_button {
        z-index: 999999 !important;
        pointer-events: auto !important;
      }

      /* Fix for popover content in dialogs */
      [data-radix-popper-content-wrapper] {
        z-index: 999999 !important;
        pointer-events: auto !important;
      }
      
      /* Ensure select content is visible and interactive */
      .select-content,
      [data-radix-select-content] {
        z-index: 999999 !important;
        pointer-events: auto !important;
      }
      
      /* Fix for checkbox interactions */
      [data-state="checked"],
      [data-state="unchecked"] {
        pointer-events: auto !important;
      }
      
      /* Force all popover content to be interactive */
      [data-radix-popover-content],
      [data-state="open"] {
        pointer-events: auto !important;
      }
      
      /* Select Items and Dropdown Items */
      [data-radix-select-item],
      [data-radix-dropdown-menu-item] {
        pointer-events: auto !important;
        cursor: pointer !important;
      }

      /* Ensure dropdown menus are visible */
      [data-radix-dropdown-menu-content] {
        z-index: 999999 !important;
        pointer-events: auto !important;
        background-color: white !important;
      }

      /* Override any transparent backgrounds in dropdown content */
      [data-radix-select-content],
      [data-radix-dropdown-menu-content],
      [data-radix-popover-content] {
        background-color: white !important;
        border: 1px solid #e2e8f0 !important;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
      }

      /* Dark mode overrides if needed */
      .dark [data-radix-select-content],
      .dark [data-radix-dropdown-menu-content],
      .dark [data-radix-popover-content] {
        background-color: #1a1a1a !important;
        border-color: #4a4a4a !important;
      }

      /* Fix for MultiSelect dropdown positioning */
      .multi-select-dropdown {
        z-index: 999999 !important;
        pointer-events: auto !important;
      }

      /* Fix for any missing interactive elements */
      .interactive-element {
        pointer-events: auto !important;
      }

      /* Ensure Popover components are clickable */
      [data-radix-popover-trigger] {
        pointer-events: auto !important;
      }
      
      /* Ensure MultiSelectChips dropdown is visible */
      .multi-select-chips-dropdown {
        z-index: 999999 !important;
        pointer-events: auto !important;
        background-color: white !important;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
      }
      
      /* Fix for date range picker calendar popups */
      .react-calendar,
      .react-calendar__viewContainer,
      .react-calendar__month-view,
      .react-calendar button {
        pointer-events: auto !important;
        z-index: 999999 !important;
      }
      
      /* Ensure all Calendar components are interactive */
      .rdp-day_selected, 
      .rdp-day, 
      .rdp-button,
      .rdp-nav {
        pointer-events: auto !important;
        z-index: 999999 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Always return light value, since we're only using light mode
  const getThemeValue = <T,>(lightValue: T, _darkValue: T): T => {
    return lightValue;
  };

  return (
    <ThemeContext.Provider value={{ 
      theme: 'light',
      getThemeValue
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
